/***************************************************************************************
 * FileName      : IntroAnimationService.ts
 * Description   : 진입 애니메이션 서비스
 *                 - intro-animation.json 데이터 기반 Sweep 경로 순회
 *                 - 각 Sweep 이동 후 뷰포인트 프리셋(카메라 방향/줌) 적용
 *                 - 건너뛰기(skip) 시 즉시 마지막 Sweep + 뷰포인트로 이동
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.02.12
 * Modified Date :
 * Reference     : packages/scjeil-smart_ict_lab-app/assets/json/matterport/intro-animation.json
 ***************************************************************************************/

// 2026.02.12 : 박은정 : 진입 애니메이션 JSON 데이터 직접 임포트
import introAnimationData from '../../assets/json/matterport/intro-animation.json';

// ============================================================================
// 진입 애니메이션 JSON 데이터 타입 정의
// ============================================================================

// 2026.02.12 : 박은정 : Sweep 경로 단계별 데이터 타입
interface SweepStep {
  step: number;
  label: string;
  sweep: {
    sweepId: string;
    position: { x: number; y: number; z: number };
  };
  viewpoint: {
    rotation: { x: number; y: number };
    zoom: number;
  };
}

// 2026.02.12 : 박은정 : 진입 애니메이션 JSON 전체 구조 타입
interface IntroAnimationData {
  _fileInfo: any;
  animationId: string;
  modelId: string;
  sweepPath: SweepStep[];
}

// ============================================================================
// IntroAnimationService 클래스
// ============================================================================

/**
 * 2026.02.12 : 박은정 : 진입 애니메이션 서비스 (싱글톤)
 * - Matterport SDK를 통해 Sweep 경로를 순차적으로 이동
 * - 각 Sweep 이동 후 뷰포인트 프리셋(카메라 방향/줌) 적용
 * - 건너뛰기(skip) 지원: 즉시 마지막 Sweep + 뷰포인트로 INSTANT 이동
 */
export class IntroAnimationService {
  private static instance: IntroAnimationService;
  // 2026.02.12 : 박은정 : Matterport SDK 인스턴스
  private sdk: any = null;
  // 2026.02.12 : 박은정 : 애니메이션 데이터 (JSON에서 로드)
  private data: IntroAnimationData = introAnimationData as IntroAnimationData;
  // 2026.02.12 : 박은정 : 취소(건너뛰기) 플래그
  private cancelled = false;
  // 2026.02.12 : 박은정 : 애니메이션 완료 여부 (한 번만 실행)
  private completed = false;
  // 2026.02.12 : 박은정 : play() 실행 중 Promise (skip에서 play 종료 대기용)
  private playPromise: Promise<boolean> | null = null;

  private constructor() {}

  // 2026.02.12 : 박은정 : 싱글톤 패턴
  static getInstance(): IntroAnimationService {
    if (!IntroAnimationService.instance) {
      IntroAnimationService.instance = new IntroAnimationService();
    }
    return IntroAnimationService.instance;
  }

  // 2026.02.12 : 박은정 : SDK 인스턴스 설정
  initialize(sdk: any): void {
    this.sdk = sdk;
  }

  // 2026.02.12 : 박은정 : 애니메이션 완료 여부 확인
  isCompleted(): boolean {
    return this.completed;
  }

  /**
   * 2026.02.12 : 박은정 : 시작 Sweep + 뷰포인트를 즉시 적용 (타이틀 모달 표시 전 호출)
   * - 타이틀 모달이 표시될 때 이미 시작 위치의 뷰포인트가 적용되어 있도록 함
   */
  async applyStartPosition(): Promise<void> {
    if (!this.sdk) return;
    const firstStep = this.data.sweepPath[0];
    if (!firstStep) return;

    try {
      // 2026.02.12 : 박은정 : 시작 Sweep으로 INSTANT 이동
      await this.sdk.Sweep.moveTo(firstStep.sweep.sweepId, {
        transition: this.sdk.Sweep.Transition.INSTANT,
        transitionTime: 0,
      });
      // 2026.02.12 : 박은정 : 시작 뷰포인트 프리셋 적용 (카메라 회전 + 줌)
      await this.sdk.Camera.setRotation(
        { x: firstStep.viewpoint.rotation.x, y: firstStep.viewpoint.rotation.y },
        { speed: 360 }
      );
      if (firstStep.viewpoint.zoom > 0) {
        await this.sdk.Camera.zoomTo(firstStep.viewpoint.zoom);
      }
    } catch (e) {
      // 에러 무시
    }
  }

  /**
   * 2026.02.12 : 박은정 : 진입 애니메이션 재생
   * - sweepPath를 순서대로 순회하며 Sweep 이동 + 뷰포인트 프리셋 적용
   * - cancelled 플래그가 true이면 즉시 중단 (skip()에서 마지막 Sweep 적용 처리)
   * - 정상 완료 시 completed = true
   *
   * @returns 정상 완료 시 true, 건너뛰기로 중단 시 false
   */
  async play(): Promise<boolean> {
    if (!this.sdk || this.completed) return true;

    this.cancelled = false;
    // 2026.02.12 : 박은정 : play 실행을 Promise로 저장 (skip에서 종료 대기용)
    this.playPromise = this._doPlay();
    return this.playPromise;
  }

  // 2026.02.12 : 박은정 : 실제 애니메이션 루프 (play()에서 호출)
  private async _doPlay(): Promise<boolean> {
    const steps = this.data.sweepPath;

    for (const step of steps) {
      if (this.cancelled) return false;

      // 2026.02.12 : 박은정 : 시작 Sweep(step 0)은 applyStartPosition()에서 이미 적용 완료 → 전체 생략
      if (step.step === 0) continue;

      // 2026.02.12 : 박은정 : Sweep으로 FLY 이동
      try {
        await this.sdk.Sweep.moveTo(step.sweep.sweepId, {
          transition: this.sdk.Sweep.Transition.FLY,
          transitionTime: 1500,
        });
      } catch (e) {
        if (this.cancelled) return false;
      }

      if (this.cancelled) return false;

      // 2026.02.12 : 박은정 : 뷰포인트 프리셋 적용 (카메라 회전)
      try {
        await this.sdk.Camera.setRotation(
          { x: step.viewpoint.rotation.x, y: step.viewpoint.rotation.y },
          { speed: 120 }
        );
      } catch (e) {
        if (this.cancelled) return false;
      }

      if (this.cancelled) return false;

      // 2026.02.12 : 박은정 : 뷰포인트 프리셋 적용 (줌)
      try {
        if (step.viewpoint.zoom > 0) {
          await this.sdk.Camera.zoomTo(step.viewpoint.zoom);
        }
      } catch (e) {
        if (this.cancelled) return false;
      }
    }

    // 2026.02.12 : 박은정 : 정상 완료
    this.completed = true;
    return true;
  }

  /**
   * 2026.02.12 : 박은정 : 진입 애니메이션 건너뛰기
   * - cancelled 플래그 설정 → play() 루프 중단 대기 → 마지막 Sweep + 뷰포인트 즉시 적용
   */
  async skip(): Promise<void> {
    if (!this.sdk || this.completed) return;

    this.cancelled = true;
    const steps = this.data.sweepPath;
    const lastStep = steps[steps.length - 1];
    if (!lastStep) return;

    // 2026.02.12 : 박은정 : play() 루프가 cancelled를 감지하고 완전히 종료될 때까지 대기
    if (this.playPromise) {
      try { await this.playPromise; } catch (e) { /* 무시 */ }
      this.playPromise = null;
    }

    // 2026.02.12 : 박은정 : 마지막 Sweep으로 INSTANT 이동 (재시도 포함)
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        await this.sdk.Sweep.moveTo(lastStep.sweep.sweepId, {
          transition: this.sdk.Sweep.Transition.INSTANT,
          transitionTime: 0,
        });
        break; // 성공 시 루프 종료
      } catch (e) {
        // 2026.02.12 : 박은정 : 이전 전환과 충돌 시 짧은 대기 후 재시도
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }

    // 2026.02.12 : 박은정 : 마지막 뷰포인트 프리셋 즉시 적용 (최대 속도)
    try {
      await this.sdk.Camera.setRotation(
        { x: lastStep.viewpoint.rotation.x, y: lastStep.viewpoint.rotation.y },
        { speed: 99999 }
      );
    } catch (e) {
      // 에러 무시
    }

    // 2026.02.12 : 박은정 : 마지막 줌 즉시 적용
    try {
      if (lastStep.viewpoint.zoom > 0) {
        await this.sdk.Camera.zoomTo(lastStep.viewpoint.zoom);
      }
    } catch (e) {
      // 에러 무시
    }

    this.completed = true;
  }
}

